import { Dispatch, SetStateAction, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { SongPreview } from '~/interfaces';
import { Button } from '~/modules/elements/akui/button';
import { Icon } from '~/modules/elements/akui/icon';
import { Input } from '~/modules/elements/input';
import useKeyboardNav from '~/modules/hooks/use-keyboard-nav';
import useSongIndex from '~/modules/songs/hooks/use-song-index';
import clearString from '~/modules/utils/clear-string';
import groupDigits from '~/modules/utils/group-digits';
import {
  CATEGORY_LABELS,
  FilterCategoryId,
} from '~/routes/sing-a-song/song-selection/components/filters/filter-options';
import useFilterCategories, {
  isMultiCategory,
} from '~/routes/sing-a-song/song-selection/components/filters/use-filter-categories';
import { PlaylistEntry } from '~/routes/sing-a-song/song-selection/hooks/use-playlists';
import { AppliedFilters } from '~/routes/sing-a-song/song-selection/hooks/use-song-list-filter';

const CATEGORIES: FilterCategoryId[] = ['playlist', 'genre', 'language', 'duets', 'played', 'length'];

/** Genre keeps the 6-wide grid it was drawn with; the rest carry longer labels and get four. */
const columnsFor = (category: FilterCategoryId) => (category === 'genre' ? 6 : 4);

interface Props {
  open: boolean;
  onClose: () => void;
  /** The whole catalogue this screen can reach, which is what the option counts are measured against. */
  catalogue: SongPreview[];
  resultCount: number;
  filters: AppliedFilters;
  setFilters: Dispatch<SetStateAction<AppliedFilters>>;
  playlists: PlaylistEntry[];
  selectedPlaylist: string | null;
  setSelectedPlaylist: (name: string) => void;
}

export default function FilterPanel({
  open,
  onClose,
  catalogue,
  resultCount,
  filters,
  setFilters,
  playlists,
  selectedPlaylist,
  setSelectedPlaylist,
}: Props) {
  const [category, setCategory] = useState<FilterCategoryId>('playlist');
  const [pane, setPane] = useState<'rail' | 'options'>('rail');
  const [genreSearch, setGenreSearch] = useState('');
  const genreSearchInput = useRef<React.ComponentRef<typeof Input>>(null);

  // Languages come from the whole index rather than from `catalogue`, which has already had the
  // excluded-languages setting applied to it - see `useFilterCategories`. The picker sheet this
  // category replaced read the index too.
  const songIndex = useSongIndex();
  const { optionsFor, selectedValues, isOptionSelected, apply, labelFor, activeFilters, clearAll, genres, languages } =
    useFilterCategories({
      categories: CATEGORIES,
      catalogue,
      languageSource: songIndex.data,
      filters,
      setFilters,
      playlists: playlists.map((playlist) => ({ name: playlist.name, label: playlist.remoteLabel ?? playlist.name })),
      selectedPlaylist,
      setSelectedPlaylist,
    });

  const visibleOptions = useMemo(() => {
    const options = optionsFor(category);
    if (category !== 'genre' || !genreSearch) return options;
    const wanted = clearString(genreSearch);

    return options.filter((option) => !option.value || clearString(option.label).includes(wanted));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- optionsFor reads the props this already depends on
  }, [category, genreSearch, genres, languages, playlists, filters, selectedPlaylist, catalogue]);

  // The hints go along the bottom rather than in the corner box: the corner box is fixed to the top
  // right at z-1000, which is exactly where this panel's result count and Clear all are, and it
  // would sit on top of both. The strip also reads better from across a room, which is the point.
  const railNav = useKeyboardNav({
    enabled: open && pane === 'rail',
    exclusive: true,
    direction: 'vertical',
    onBackspace: onClose,
    backspaceHelp: 'Close',
    title: 'Filter',
    presentation: 'strip',
  });
  const optionsNav = useKeyboardNav({
    enabled: open && pane === 'options',
    exclusive: true,
    direction: 'horizontal',
    onBackspace: () => setPane('rail'),
    backspaceHelp: 'Back to the rail',
    title: 'Filter',
    presentation: 'strip',
  });

  const columns = columnsFor(category);
  // The fade at the foot of the grid says "there is more below", so it is only drawn when there is:
  // Duets has two options and Playlist six, and fading their bottom row says something untrue.
  const optionsGrid = useRef<HTMLDivElement>(null);
  const [optionsScrollable, setOptionsScrollable] = useState(false);
  useLayoutEffect(() => {
    const grid = optionsGrid.current;
    // Rows come out a sub-pixel taller than the box that holds them, so "more than a pixel or two"
    // rather than "any overflow at all": Duets' single row of two options overflows by 1px.
    setOptionsScrollable(grid !== null && grid.scrollHeight - grid.clientHeight > 8);
  }, [category, columns, visibleOptions.length]);
  const enterOptions = () => {
    setPane('options');
    optionsNav.focusElement(`filter-${category}-option-${visibleOptions[0]?.value ?? ''}`);
  };
  const jumpRow = (direction: 1 | -1) => {
    const names = visibleOptions.map((option) => `filter-${category}-option-${option.value}`);
    const index = names.indexOf(optionsNav.focused ?? '');
    if (index === -1) return;
    const next = Math.min(Math.max(index + direction * columns, 0), names.length - 1);
    optionsNav.focusElement(names[next]);
  };

  // `optionsNav.focused` has to be in the deps: react-hotkeys-hook holds the callback it was last
  // given, so without it a row jump measures from wherever the focus was when the deps last changed
  // rather than from where it is now - every jump lands the same distance from the top of the grid.
  const jumpDeps = [open, pane, category, visibleOptions, optionsNav.focused];
  useHotkeys('right', enterOptions, { enabled: open && pane === 'rail' }, jumpDeps);
  useHotkeys('up', () => jumpRow(-1), { enabled: open && pane === 'options' }, jumpDeps);
  useHotkeys('down', () => jumpRow(1), { enabled: open && pane === 'options' }, jumpDeps);

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-99 bg-black/55" onClick={onClose} data-test="filters-backdrop" />
      <div
        data-test="filters-panel"
        className="fixed top-[7.5rem] left-1/2 z-101 flex max-h-[calc(100vh-12rem)] w-full max-w-360 -translate-x-1/2 flex-col overflow-hidden rounded-2xl border border-white/12 bg-slate-800 shadow-[0_40px_100px_rgba(0,0,0,0.7)]">
        <div className="flex flex-wrap items-center gap-3.5 border-b border-white/10 px-7 py-5.5">
          <span className="text-active stroke-text text-[1.875rem] font-bold">Filter</span>
          {activeFilters.map((id) => (
            <button
              key={id}
              type="button"
              onClick={() => apply(id, '')}
              data-test={`filter-pill-${id}`}
              aria-label={`Remove the ${CATEGORY_LABELS[id]} filter`}
              className="bg-active/18 flex h-[2.375rem] cursor-pointer items-center gap-2.5 rounded-full px-3.5 text-[1.125rem] font-bold uppercase shadow-[inset_0_0_0_2px_rgba(255,165,0,.5)]">
              {labelFor(id)}
              <Icon icon="ic:baseline-close" size={4} className="opacity-60" />
            </button>
          ))}
          <span className="ml-auto text-[1.25rem] font-bold text-white/60" data-test="filters-result-count">
            {groupDigits(resultCount)} of {groupDigits(catalogue.length)} songs
          </span>
          <Button
            size="small"
            fullWidth={false}
            className="animate-none px-5"
            {...railNav.register('filters-clear', clearAll, 'Clear all')}>
            Clear all
          </Button>
        </div>

        <div className="flex min-h-0 items-stretch">
          <div className="flex w-[18.75rem] shrink-0 flex-col gap-2.5 border-r border-white/10 p-5">
            {CATEGORIES.map((id) => {
              const navProps = railNav.register(
                `filter-${id}`,
                () => {
                  setCategory(id);
                  setGenreSearch('');
                },
                CATEGORY_LABELS[id],
                // Opening the panel lands on the category whose options are showing, not on Clear
                // all - which is registered first, and would otherwise take the focus and read as
                // though clearing were the thing being offered.
                category === id,
              );
              const isOpen = category === id;
              const isSet = id !== 'playlist' && selectedValues(id).length > 0;

              return (
                <button
                  key={id}
                  type="button"
                  {...navProps}
                  className={`flex h-[3.875rem] cursor-pointer flex-col justify-center gap-px rounded px-4 text-left ${
                    isOpen
                      ? 'bg-active/16 shadow-[inset_0_0_0_2px_rgb(255,165,0)]'
                      : 'bg-black/45 shadow-[inset_0_0_0_1px_rgba(255,255,255,.1)]'
                  } ${navProps.focused ? 'subtle-focus' : ''}`}>
                  <span className={`text-[1.1875rem] font-bold uppercase ${isOpen ? 'text-white' : 'text-white/80'}`}>
                    {CATEGORY_LABELS[id]}
                  </span>
                  <span className={`truncate text-[0.9375rem] ${isSet ? 'text-active' : 'text-white/40'}`}>
                    {labelFor(id)}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="flex min-w-0 flex-1 flex-col gap-4 px-6 py-5">
            {category === 'genre' && (
              <div
                {...optionsNav.register(
                  'filter-genre-search',
                  () => genreSearchInput.current?.element?.focus(),
                  'Find a genre',
                )}>
                <Input
                  ref={genreSearchInput}
                  focused={false}
                  label={<Icon icon="ic:baseline-search" size={5} />}
                  value={genreSearch}
                  onChange={setGenreSearch}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || (e.key === 'Backspace' && !genreSearch)) {
                      genreSearchInput.current?.element?.blur();
                    }
                  }}
                  placeholder="Find a genre"
                  data-test="filter-genre-search-input"
                />
              </div>
            )}
            <div
              ref={optionsGrid}
              className="grid max-h-[26.25rem] [scrollbar-width:none] gap-2.5 overflow-y-auto [&::-webkit-scrollbar]:hidden"
              style={{
                gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                maskImage: optionsScrollable
                  ? 'linear-gradient(to bottom, black calc(100% - 2rem), transparent)'
                  : undefined,
              }}
              data-test={`filter-${category}-options`}>
              {visibleOptions.map((option) => {
                const name = `filter-${category}-option-${option.value}`;
                const navProps = optionsNav.register(name, () => apply(category, option.value), option.label);
                const isSelected = isOptionSelected(category, option.value);

                return (
                  <Button
                    key={name}
                    size="small"
                    fullWidth={false}
                    className="h-[3.25rem]! animate-none justify-between px-3.5 text-[1.1875rem]"
                    {...navProps}
                    // Accent fill says "this one is on"; the ring says only "Enter would pick this".
                    focused={isSelected || !!navProps.focused}
                    subtleFocused={!isSelected && !!navProps.focused}
                    // Said on an attribute as well as in colour, the way the phone's sheet does it:
                    // the fill is shared with the keyboard ring, and `isE2E()` suppresses both.
                    data-selected={isSelected}>
                    <span className="truncate">{option.label}</span>
                    {option.count !== undefined && (
                      <span className="pl-2 text-[1.1875rem] tabular-nums opacity-55">{option.count}</span>
                    )}
                  </Button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4 border-t border-white/10 px-7 py-3 text-base text-white/45">
          <span className="flex items-center gap-1.5">
            <Hint>Back</Hint> closes — filters apply as you pick them
          </span>
          <span className="flex items-center gap-1.5">
            <Hint>▶</Hint> options, <Hint>◀ ▶</Hint> moves, <Hint>Back</Hint> returns to the rail
          </span>
          {/* Said out loud because nothing else on the panel does: picking a second genre is not
              the obvious reading of a grid where one option is already filled in. */}
          {isMultiCategory(category) && (
            <span className="ml-auto flex items-center gap-1.5">Pick as many as you like — Any clears them</span>
          )}
        </div>
      </div>
    </>
  );
}

const Hint = ({ children }: { children: React.ReactNode }) => (
  <span className="rounded border border-white/25 px-2 py-px text-sm">{children}</span>
);
